/**
 * Zod schemas for the subscription-related API endpoints consumed by
 * `@/lib/api/apiClient`'s TanStack Query hooks (Requirement #4 of
 * "Unified API client with RTK Query for all frontend data fetching":
 * "Ensure all API request and response types are defined with Zod
 * schemas in src/lib/validation/ and used for runtime validation and
 * TypeScript inference").
 *
 * Two distinct backend shapes, both real/live in this codebase:
 *
 * 1. `subscriptionMeSchema` — `GET /api/subscriptions/me`
 *    (`functions/api/subscriptions/me.ts`'s `SubscriptionMeDTO` /
 *    `subscriptionMeResponseSchema`). The authenticated customer's own
 *    plan/status/expiry/jobs-limit. Duplicated here (not cross-imported
 *    from `functions/api/**`) because `apps/web` is its own workspace
 *    package with its own `tsconfig`/build and cannot import across to
 *    repo-root `functions/` at runtime — same convention as
 *    `@/lib/utils.ts` owning its own copy of the root `src/` tree's `cn`
 *    helper. Field-for-field identical to the real DTO; see that file
 *    for the authoritative shape.
 *
 * 2. `adminSubscriptionSchema` / `adminSubscriptionsListSchema` —
 *    `GET /api/admin/subscriptions`
 *    (`apps/workers/src/api/admin/subscriptions.ts`), matching
 *    `AdminSubscriptionStateInfo` / `AdminListResponse<T>` from
 *    `@herohire/types` (`packages/types/src/admin.ts`) — the admin
 *    "total visibility" subscriptions dashboard
 *    (`app/routes/admin/Subscriptions.tsx`).
 *
 * Related: GitHub issue #170 (`functions/api/subscriptions/me.ts`),
 * GitHub issue #166 (`apps/workers/src/api/admin/subscriptions.ts`),
 * docs/v2/04-stripe-billing.md §3.3/§4
 */

import { z } from 'zod';

// ========================================================================
// GET /api/subscriptions/me
// ========================================================================

/**
 * This app's real, persisted `subscription_status` DB enum (UPPERCASE —
 * see `db/schema.ts`'s `subscriptionStatusEnum`), not an invented
 * lowercase union.
 */
export const subscriptionStatusSchema = z.enum([
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
  'UNPAID',
]);
export type AppSubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

/**
 * Billing-plan tier ('basic' | 'pro' | 'enterprise', GitHub issue #169),
 * snapshotted onto `subscriptions.tier` — a generic, marketing-name-
 * independent label (distinct from `planName`) the account/billing screen
 * renders as e.g. "Plan: Pro — Active" (GitHub issue "Checkout Sessions +
 * stored keys — kill the email-heuristic linkage").
 */
export const subscriptionPlanTierSchema = z.enum(['basic', 'pro', 'enterprise']);
export type SubscriptionPlanTier = z.infer<typeof subscriptionPlanTierSchema>;

/**
 * `GET /api/subscriptions/me` response shape — mirrors
 * `functions/api/subscriptions/me.ts`'s `subscriptionMeResponseSchema`/
 * `SubscriptionMeDTO` exactly.
 */
export const subscriptionMeSchema = z.object({
  planName: z.string(),
  status: subscriptionStatusSchema,
  planTier: subscriptionPlanTierSchema,
  expiresAt: z.string().nullable(),
  /** `null` = unlimited (explicit sentinel — GitHub issue #242/#559). */
  currentJobsLimit: z.number().int().nonnegative().nullable(),
});
export type SubscriptionMeDTO = z.infer<typeof subscriptionMeSchema>;

// ========================================================================
// POST /api/stripe/checkout-session
// ========================================================================

/**
 * `POST /api/stripe/checkout-session` response shape — mirrors
 * `src/types/stripe.ts`'s `CreateCheckoutSessionResponse` (root `src/`
 * tree; duplicated here per this file's own "two distinct workspaces"
 * convention documented in the module doc-comment above).
 */
export const createCheckoutSessionResponseSchema = z.object({
  sessionId: z.string(),
  /** The Stripe-hosted Checkout URL (`https://checkout.stripe.com/...`) to
   * redirect the browser to directly — `null` only if Stripe itself
   * returned no `url` on the created session. */
  url: z.string().nullable(),
});
export type CreateCheckoutSessionResponse = z.infer<typeof createCheckoutSessionResponseSchema>;

// ========================================================================
// GET /api/admin/subscriptions
// ========================================================================

export const siloModeSchema = z.enum(['SHARED', 'ENTERPRISE']);
export type AdminSubscriptionSiloMode = z.infer<typeof siloModeSchema>;

/**
 * Mirrors `AdminSubscriptionStateInfo` (`@herohire/types`,
 * `packages/types/src/admin.ts`) — the real Stripe-derived 5-value
 * status enum, not the coarser legacy `AdminSubscriptionInfo` shape.
 */
export const adminSubscriptionSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  customerName: z.string().nullable().optional(),
  customerEmail: z.string().nullable().optional(),
  planId: z.string().nullable().optional(),
  planName: z.string().nullable().optional(),
  subscriptionStatus: subscriptionStatusSchema,
  siloMode: siloModeSchema,
  expiresAt: z.string(),
  /**
   * GitHub issue #242/#559 — `null` = unlimited (explicit sentinel).
   * Optional so older cached responses (pre-this-issue) still validate.
   */
  currentJobsLimit: z.number().int().nullable().optional(),
  /** The row's plan's `jobs_limit` — `null` = the plan itself is unlimited. */
  planJobsLimit: z.number().int().nullable().optional(),
  stripeCustomerId: z.string(),
  stripeSubscriptionId: z.string(),
  cancelAtPeriodEnd: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminSubscriptionStateInfo = z.infer<typeof adminSubscriptionSchema>;

/**
 * Mirrors `AdminListResponse<T>` (`@herohire/types`) applied to
 * `AdminSubscriptionStateInfo`.
 */
export const adminSubscriptionsListSchema = z.object({
  data: z.array(adminSubscriptionSchema),
  total_count: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
  has_more: z.boolean(),
});
export type AdminSubscriptionsListResponse = z.infer<typeof adminSubscriptionsListSchema>;

/**
 * Filter/sort/pagination params accepted by `GET /api/admin/subscriptions`.
 * Validated client-side before being serialized to a query string so a
 * caller can never send a malformed `sort_by`/`sort_order`/status value.
 */
export const adminSubscriptionsQuerySchema = z.object({
  limit: z.number().int().positive().default(20),
  offset: z.number().int().nonnegative().default(0),
  sortBy: z
    .enum(['customer_name', 'plan', 'subscription_status', 'expires_at', 'silo_mode'])
    .default('expires_at'),
  sortOrder: z.enum(['asc', 'desc']).default('asc'),
  status: z.union([subscriptionStatusSchema, z.literal('')]).default(''),
  siloMode: z.union([siloModeSchema, z.literal('')]).default(''),
});
export type AdminSubscriptionsQueryParams = z.infer<typeof adminSubscriptionsQuerySchema>;

/**
 * Serializes validated query params to the `URLSearchParams` string
 * `GET /api/admin/subscriptions` expects (`limit`/`offset`/`sort_by`/
 * `sort_order`/optional `status`/`silo_mode`).
 */
export function buildAdminSubscriptionsQueryString(params: AdminSubscriptionsQueryParams): string {
  const query = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
    sort_by: params.sortBy,
    sort_order: params.sortOrder,
  });
  if (params.status) query.set('status', params.status);
  if (params.siloMode) query.set('silo_mode', params.siloMode);
  return query.toString();
}

// ========================================================================
// GET /api/admin/subscription-plans — billing plan catalog (issue #197)
// ========================================================================

/**
 * Mirrors `AdminSubscriptionPlanInfo` (`@herohire/types`,
 * `packages/types/src/admin.ts`) — the `subscription_plans` billing
 * catalog itself (name/Stripe Price ID/price), distinct from
 * `adminSubscriptionSchema` above (a per-customer subscription row).
 * GitHub issue #197: "subscription_plans has placeholder Stripe price
 * IDs -> every paying customer provisioned on FREE Basic plan."
 */
export const adminSubscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  priceCents: z.number().int(),
  stripePriceId: z.string(),
  tier: z.enum(['basic', 'pro', 'enterprise']),
  siloMode: siloModeSchema,
  jobsLimit: z.number().int().nullable(),
  isPlaceholderPriceId: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type AdminSubscriptionPlanInfo = z.infer<typeof adminSubscriptionPlanSchema>;

export const adminSubscriptionPlansListSchema = z.object({
  data: z.array(adminSubscriptionPlanSchema),
  total_count: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
  has_more: z.boolean(),
});
export type AdminSubscriptionPlansListResponse = z.infer<typeof adminSubscriptionPlansListSchema>;

// ========================================================================
// GET /api/admin/unlinked-subscriptions — bug: "Stripe webhook — paying
// customer has NO user record, subscription creation failing since Jun 9"
// ========================================================================

/**
 * Mirrors `AdminUnlinkedSubscriptionInfo` (`@herohire/types`,
 * `packages/types/src/admin.ts`) — a `stripe_pending_linkages` row: a
 * Stripe subscription webhook whose `stripe_customers` lookup failed on
 * every resolution retry (`apps/workers/src/api/webhooks/stripe.ts`),
 * surfaced so an admin can manually link the Stripe customer to the
 * correct account instead of the paid subscription silently vanishing
 * into Sentry logs. Sentry evidence: subscription
 * `sub_1TgNV6Lj957TeqndmPVByhfE`.
 */
export const adminUnlinkedSubscriptionSchema = z.object({
  id: z.string(),
  stripeSubscriptionId: z.string(),
  stripeCustomerId: z.string().nullable(),
  checkoutEmail: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  status: z.enum(['unlinked', 'linked']),
  createdAt: z.string(),
  resolvedAt: z.string().nullable(),
  linkedUserId: z.string().nullable(),
});
export type AdminUnlinkedSubscriptionInfo = z.infer<typeof adminUnlinkedSubscriptionSchema>;

export const adminUnlinkedSubscriptionsListSchema = z.object({
  data: z.array(adminUnlinkedSubscriptionSchema),
  total_count: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
  has_more: z.boolean(),
});
export type AdminUnlinkedSubscriptionsListResponse = z.infer<
  typeof adminUnlinkedSubscriptionsListSchema
>;
